/**
 * Infocard bei Klick auf Planeten.
 * Desktop: schwebt neben Klickposition. Mobile: Slide-In vom rechten Rand.
 */
import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { Camera } from '../renderer/Camera';
import type { PlanetInfo } from '../world/PlayerWorldTypes';
import type { SolarEnergyEntry } from '../systems/SolarEnergyTypes';
import { axialKey, axialToWorld } from '../math/HexMath';
import { worldToScreen } from '../math/ProjectionUtils';
import {
  PLANET_NAMES, PLANET_ICONS, OWNER_COLOR, MEEPLE_SVG,
  ownerLabel, energyBar, resourceBar,
} from './PlanetDetailContent';
import { initTooltip } from './Tooltip';
import { isMobile } from './UITheme';

const PANEL_W = 'clamp(220px,38vw,280px)';
const OFFSET = 16;
const SLIDE_MS = 250;
const HOLO_BG = 'linear-gradient(180deg,rgba(8,20,45,0.93),rgba(4,12,28,0.9))';
const HOLO_BRD = '1px solid rgba(100,180,255,0.25)';
const HOLO_GLOW = '-4px 0 20px rgba(80,160,255,0.12),inset 0 1px 0 rgba(100,180,255,0.12)';
const SCAN = 'repeating-linear-gradient(0deg,transparent,transparent 3px,rgba(100,180,255,0.02) 3px,rgba(100,180,255,0.02) 4px)';
const CLOSE = '<div style="display:flex;justify-content:flex-end;margin-bottom:2px">' +
  '<span data-close style="cursor:pointer;color:rgba(100,180,255,0.4);font-size:18px;' +
  'line-height:1;padding:4px 2px">\u2715</span></div>';

interface HexClickEvent { q: number; r: number; screenX: number; screenY: number }

export class PlanetDetailPanel implements GameModule {
  readonly name = 'PlanetDetailPanel';

  private _planets: Map<number, PlanetInfo>;
  private _energyMap: ReadonlyMap<string, SolarEnergyEntry> | null = null;
  private _el!: HTMLDivElement;
  private _visible = false;
  private _devMode: boolean;
  private _camera: Camera;
  private _canvas!: HTMLCanvasElement;
  private _hexRadius: number;
  private _worldX = 0;
  private _worldZ = 0;
  private _mobile = false;
  private _anim: Animation | null = null;
  private _goingToHub = false;

  constructor(planets: PlanetInfo[], camera: Camera, hexRadius: number, devMode = false) {
    this._devMode = devMode;
    this._camera = camera;
    this._hexRadius = hexRadius;
    this._planets = new Map();
    for (const p of planets) this._planets.set(axialKey(p.axialQ, p.axialR), p);
  }

  private _events!: import('../core/EventBus').EventBus;

  init(engine: Engine): void {
    initTooltip();
    this._events = engine.events;
    this._canvas = engine.canvas;
    this._mobile = isMobile();
    this._el = document.createElement('div');

    Object.assign(this._el.style, {
      position: 'fixed', fontFamily: 'monospace',
      fontSize: 'clamp(10px,2.4vw,12px)', color: '#8cf',
      zIndex: '9999', display: 'none',
    });

    if (this._mobile) {
      Object.assign(this._el.style, {
        top: '0', right: '0', bottom: '0', left: 'auto',
        width: 'clamp(220px,55vw,300px)', borderRadius: '10px 0 0 10px',
        background: SCAN + ',' + HOLO_BG, border: 'none', borderLeft: HOLO_BRD,
        boxShadow: HOLO_GLOW,
        overflowY: 'auto', pointerEvents: 'auto',
        padding: '10px 14px',
      });
    } else {
      Object.assign(this._el.style, {
        background: 'rgba(5,8,18,0.92)', border: '1px solid rgba(136,204,255,0.2)',
        borderRadius: '10px', padding: 'clamp(10px,2vw,14px) clamp(12px,2.5vw,18px)',
        width: PANEL_W, pointerEvents: 'none',
      });
    }
    document.body.appendChild(this._el);

    engine.events.on<ReadonlyMap<string, SolarEnergyEntry>>('solar:energy-update', (data) => {
      this._energyMap = data;
    });
    engine.events.on<HexClickEvent>('hex:clicked', (d) => {
      const planet = this._planets.get(axialKey(d.q, d.r));
      if (planet) this._show(planet);
      else this._hide();
    });
  }

  update(): void {
    if (!this._visible || this._mobile) return;
    const sp = worldToScreen(this._camera, this._canvas, this._worldX, 0, this._worldZ);
    if (!sp || sp.x < 0 || sp.x > window.innerWidth || sp.y < 0 || sp.y > window.innerHeight) {
      this._hide(); return;
    }
    this._positionAt(sp.x, sp.y);
  }

  dispose(): void { this._el.remove(); }

  private _show(p: PlanetInfo): void {
    const [wx, wz] = axialToWorld(p.axialQ, p.axialR, this._hexRadius);
    this._worldX = wx; this._worldZ = wz;

    const icon = PLANET_ICONS[p.planetType] ?? '\u{1FA90}';
    const name = PLANET_NAMES[p.planetType] ?? p.planetType;
    const color = OWNER_COLOR[p.ownership];
    const known = this._devMode || p.ownership === 'own';
    const traits: string[] = [];
    if (p.moonCount > 0) traits.push(p.moonCount === 1 ? '1 Mond' : `${p.moonCount} Monde`);
    if (p.hasRings) traits.push('Ringe');
    if (p.orbitalRing > 0) traits.push(`Orbit ${p.orbitalRing}`);
    const energy = this._energyMap?.get(p.id)?.solarEnergy ?? null;

    const html =
      `<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">` +
      `<span style="font-size:15px;letter-spacing:1px">${icon} ${name}</span>` +
      `<span title="${ownerLabel(p.ownership)}">${MEEPLE_SVG(color)}</span></div>` +
      `<div style="color:#556;font-size:11px;margin-bottom:10px">` +
      `Q ${p.axialQ} \u00b7 R ${p.axialR}` +
      (traits.length ? ` \u00b7 ${traits.join(' \u00b7 ')}` : '') + `</div>` +
      energyBar(energy, known) +
      resourceBar('Feronium', p.feronium, p.feroniumCap, '#f80', known) +
      resourceBar('Chrysalium', p.chrysalium, p.chrysaliumCap, '#0cf', known) +
      resourceBar('Neurogel', p.neurogel, p.neurogelCap, '#0f8', known) +
      `<div style="margin-top:12px;text-align:center">` +
      `<button data-open style="pointer-events:auto;padding:clamp(8px,2vw,10px) clamp(14px,3vw,16px);` +
      `background:rgba(136,204,255,0.2);color:#bdf;` +
      `border:1px solid rgba(136,204,255,0.4);border-radius:6px;cursor:pointer;` +
      `font-size:clamp(10px,2.4vw,12px);min-height:44px">Zum Hub</button></div>`;

    this._el.innerHTML = (this._mobile ? CLOSE : '') + html;

    this._el.querySelector('[data-open]')?.addEventListener('click', () => {
      this._goingToHub = true;
      this._hide();
      this._events.emit('planet:open', { planet: p });
    });
    if (this._mobile) {
      this._el.querySelector('[data-close]')?.addEventListener('click', () => this._hide());
    }

    this._el.style.display = 'block';
    this._visible = true;
    let pf = 0;
    if (this._mobile) {
      const pw = Math.min(300, Math.max(220, window.innerWidth * 0.55));
      pf = pw / window.innerWidth;
    }
    this._events.emit('camera:preview-planet', { axialQ: p.axialQ, axialR: p.axialR, panelFraction: pf });

    if (this._mobile) {
      this._anim?.cancel();
      this._anim = this._el.animate(
        [{ transform: 'translateX(100%)' }, { transform: 'translateX(0)' }],
        { duration: SLIDE_MS, easing: 'cubic-bezier(0.22,0.85,0.32,1)', fill: 'forwards' },
      );
    } else {
      this._el.style.opacity = '1';
      const sp = worldToScreen(this._camera, this._canvas, wx, 0, wz);
      if (sp) requestAnimationFrame(() => this._positionAt(sp.x, sp.y));
    }
  }

  private _positionAt(sx: number, sy: number): void {
    if (this._mobile) return;
    this._el.style.transform = '';
    const h = this._el.offsetHeight;
    this._el.style.left = (sx + OFFSET) + 'px';
    this._el.style.top = (sy - h / 2) + 'px';
  }

  private _hide(): void {
    if (!this._visible) return;
    this._visible = false;
    if (!this._goingToHub) this._events.emit('camera:preview-cancel', null);
    this._goingToHub = false;
    if (this._mobile) {
      this._anim?.cancel();
      this._anim = this._el.animate(
        [{ transform: 'translateX(0)' }, { transform: 'translateX(100%)' }],
        { duration: SLIDE_MS * 0.7, easing: 'ease-in', fill: 'forwards' },
      );
      this._anim.onfinish = () => { this._el.style.display = 'none'; this._anim = null; };
    } else {
      this._el.style.opacity = '0';
      setTimeout(() => { if (!this._visible) this._el.style.display = 'none'; }, 200);
    }
  }
}
